import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Star, Users, Clock, Briefcase, Coffee, ShowerHead as Shower } from 'lucide-react';

export function ProjectsPage() {
  const { t, language } = useLanguage();

  const mainProject = {
    title: 'Volksparkstadion',
    description: 'Hamburger Sport-Verein e.V.',
    image: 'https://images.unsplash.com/photo-1577223625816-7546f13df25d?auto=format&fit=crop&q=80',
    services: [
      {
        icon: Star,
        title: language === 'de' ? 'VIP West' : language === 'tr' ? 'VIP Batı' : 'VIP West',
        description: language === 'de' 
          ? 'VIP-Bereich Reinigung und Aufsicht'
          : language === 'tr'
          ? 'VIP bölümü temizliği ve denetimi'
          : 'VIP area cleaning and supervision',
        stats: [
          { 
            label: language === 'de' ? 'Kapazität' : language === 'tr' ? 'Kapasite' : 'Capacity',
            value: '1.650'
          }
        ],
        phases: [
          { 
            title: language === 'de' 
              ? 'Vor dem Spiel'
              : language === 'tr'
              ? 'Maç Öncesi'
              : 'Pre-match'
          },
          { 
            title: language === 'de'
              ? 'Während des Spiels'
              : language === 'tr'
              ? 'Maç Sırası'
              : 'During match'
          },
          { 
            title: language === 'de'
              ? 'Nach dem Spiel'
              : language === 'tr'
              ? 'Maç Sonrası'
              : 'Post-match'
          }
        ]
      },
      {
        icon: Star,
        title: language === 'de' ? 'VIP Ost' : language === 'tr' ? 'VIP Doğu' : 'VIP East',
        description: language === 'de'
          ? 'VIP-Bereich Reinigung und Aufsicht'
          : language === 'tr'
          ? 'VIP bölümü temizliği ve denetimi'
          : 'VIP area cleaning and supervision',
        stats: [
          {
            label: language === 'de' ? 'Kapazität' : language === 'tr' ? 'Kapasite' : 'Capacity',
            value: '1.680'
          }
        ],
        phases: [
          {
            title: language === 'de'
              ? 'Vor dem Spiel'
              : language === 'tr'
              ? 'Maç Öncesi'
              : 'Pre-match'
          },
          {
            title: language === 'de'
              ? 'Während des Spiels'
              : language === 'tr'
              ? 'Maç Sırası'
              : 'During match'
          },
          {
            title: language === 'de'
              ? 'Nach dem Spiel'
              : language === 'tr'
              ? 'Maç Sonrası'
              : 'Post-match'
          }
        ]
      },
      {
        icon: Shower,
        title: language === 'de' ? 'Campus Einrichtungen' : language === 'tr' ? 'Campus Tesisleri' : 'Campus Facilities',
        description: language === 'de'
          ? 'Tägliche Reinigung und Pflege der Umkleideräume und Duschbereiche'
          : language === 'tr'
          ? 'Soyunma odaları ve duş alanlarının günlük temizliği ve bakımı'
          : 'Daily cleaning and maintenance of changing rooms and shower areas',
        stats: [],
        cleaning: language === 'de' ? 'Unterhaltsreinigung' : language === 'tr' ? 'Günlük Temizlik' : 'Daily Cleaning'
      }
    ]
  };

  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl">
            {mainProject.title}
          </h1>
          <p className="mt-4 text-xl text-gray-600">
            {mainProject.description}
          </p>
        </div>

        <div className="mt-12">
          <img
            src={mainProject.image}
            alt={mainProject.title}
            className="w-full h-96 object-cover rounded-lg shadow-xl"
          />
        </div>

        <div className="mt-20">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            {language === 'de' 
              ? 'Unsere Servicebereiche'
              : language === 'tr'
              ? 'Hizmet Alanlarımız'
              : 'Our Service Areas'}
          </h2>
          <div className="grid grid-cols-1 gap-12">
            {mainProject.services.map((service) => {
              const Icon = service.icon;
              return (
                <div
                  key={service.title}
                  className="bg-white p-8 rounded-lg shadow-lg border border-gray-100 hover:shadow-xl transition-shadow"
                >
                  <div className="flex items-center mb-6">
                    <Icon className="h-8 w-8 text-blue-900" />
                    <h3 className="ml-4 text-2xl font-semibold text-gray-900">
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 mb-6">
                    {service.description}
                  </p>
                  {service.stats.length > 0 && (
                    <div className="grid grid-cols-2 gap-4 mb-8">
                      {service.stats.map((stat) => (
                        <div key={stat.label} className="text-center">
                          <p className="text-lg font-semibold text-blue-900">
                            {stat.value}
                          </p>
                          <p className="text-sm text-gray-500">
                            {stat.label}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  {service.phases ? (
                    <div className="border-t pt-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">
                        {language === 'de'
                          ? 'Reinigungsphasen'
                          : language === 'tr'
                          ? 'Temizlik Fazları'
                          : 'Cleaning Phases'}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {service.phases.map((phase) => (
                          <div key={phase.title} className="bg-gray-50 p-4 rounded-lg">
                            <h5 className="font-semibold text-blue-900">{phase.title}</h5>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="border-t pt-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">
                        {language === 'de'
                          ? 'Reinigungsart'
                          : language === 'tr'
                          ? 'Temizlik Türü'
                          : 'Cleaning Type'}
                      </h4>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-semibold text-blue-900">{service.cleaning}</h5>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}